<template>
  <b-card
    no-body
    class="agendamentos-card"
  >
    <!-- Header -->
    <b-card-title class="p-3 border-bottom">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="CalendarIcon"
            size="20"
            class="mr-2 text-primary"
          />
          <span>Agendamentos do Dia</span>
        </div>
        <b-badge
          v-if="agendamentosTotal > 0"
          pill
          variant="primary"
        >
          {{ agendamentosTotal }}
        </b-badge>
      </div>
    </b-card-title>

    <!-- Loading State -->
    <div
      v-if="loading"
      class="p-4 text-center"
    >
      <b-spinner small />
      <p class="mt-2 mb-0 text-muted">
        Carregando agendamentos...
      </p>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="agendamentosTotal === 0"
      class="p-4 text-center"
    >
      <feather-icon
        icon="CheckCircleIcon"
        size="32"
        class="text-success mb-2"
      />
      <p class="mb-0 text-muted">
        Nenhum agendamento para hoje
      </p>
    </div>

    <!-- Agendamentos List -->
    <vue-perfect-scrollbar
      v-else
      :settings="scrollSettings"
      class="scrollable-container"
      style="max-height: 550px"
    >
      <div class="agendamentos-list">
        <div
          v-for="agendamento in agendamentosPorProximidade"
          :key="agendamento.id"
          class="agendamento-item cursor-pointer"
          :class="getClasseProximidade(agendamento.data_agendamento)"
          @click="irParaNegociacao(agendamento.cliente_id)"
        >
          <div class="d-flex align-items-start">
            <!-- Avatar com indicador de proximidade -->
            <b-avatar
              :variant="getVarianteAvatar(agendamento.data_agendamento)"
              size="sm"
              class="mr-3 flex-shrink-0"
            >
              <feather-icon
                icon="ClockIcon"
                size="14"
              />
            </b-avatar>

            <!-- Conteúdo -->
            <div class="flex-grow-1 min-width-0">
              <div class="d-flex align-items-start justify-content-between">
                <div>
                  <p class="mb-1 font-weight-600 text-truncate">
                    {{ formatCliente(agendamento.cliente) }}
                  </p>
                  <div class="d-flex align-items-center flex-wrap mt-1">
                    <b-badge
                      :variant="getStatusBadgeVariant(agendamento.status)"
                      class="mr-1 mb-1"
                    >
                      {{ getStatusLabel(agendamento.status) }}
                    </b-badge>
                    <b-badge
                      v-if="agendamento.motivo"
                      :variant="getMotivoBadgeVariant(agendamento.motivo)"
                      class="mb-1"
                    >
                      {{ getMotivoLabel(agendamento.motivo) }}
                    </b-badge>
                  </div>
                </div>
                <b-badge
                  :variant="getBadgeProximidade(agendamento.data_agendamento)"
                  class="ml-2 flex-shrink-0"
                >
                  {{ getProximidadeLabel(agendamento.data_agendamento) }}
                </b-badge>
              </div>

              <!-- Horário -->
              <div class="mt-2 d-flex align-items-center">
                <feather-icon
                  icon="MapPinIcon"
                  size="12"
                  class="text-muted mr-1"
                />
                <small class="text-muted">
                  {{ formatarHora(agendamento.data_agendamento) }}
                </small>
              </div>

              <!-- Atendente -->
              <div class="mt-1 d-flex align-items-center">
                <feather-icon
                  icon="UserIcon"
                  size="12"
                  class="text-muted mr-1"
                />
                <small class="text-muted">
                  {{ agendamento.atendente_nome || 'Sistema' }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </vue-perfect-scrollbar>

    <!-- Footer com filtros -->
    <div class="border-top p-3">
      <div class="d-flex gap-2">
        <b-badge
          v-if="contadorProximidade.vencido > 0"
          variant="secondary"
          class="cursor-pointer"
          pill
        >
          ❌ {{ contadorProximidade.vencido }}
        </b-badge>
        <b-badge
          v-if="contadorProximidade.critico > 0"
          variant="danger"
          class="cursor-pointer"
          pill
        >
          🚨 {{ contadorProximidade.critico }}
        </b-badge>
        <b-badge
          v-if="contadorProximidade.urgente > 0"
          variant="warning"
          class="cursor-pointer"
          pill
        >
          ⚠️ {{ contadorProximidade.urgente }}
        </b-badge>
        <b-badge
          v-if="contadorProximidade.aviso > 0"
          variant="info"
          class="cursor-pointer"
          pill
        >
          ⏰ {{ contadorProximidade.aviso }}
        </b-badge>
        <b-badge
          v-if="contadorProximidade.normal > 0"
          variant="primary"
          class="cursor-pointer"
          pill
        >
          📅 {{ contadorProximidade.normal }}
        </b-badge>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BBadge, BAvatar, BSpinner,
} from 'bootstrap-vue'
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { differenceInMinutes, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import agendamentosService from '@/services/agendamentos'
import { getStatusLabel, getStatusBadgeClass, getMotivoLabel, getMotivoBadgeClass, formatCliente } from '@/views/apps/calendar-pessoal/calendar-utils'

const badgeVariantMap = {
  'badge-info': 'info',
  'badge-warning': 'warning',
  'badge-success': 'success',
  'badge-danger': 'danger',
  'badge-secondary': 'secondary',
}

export default {
  components: {
    BCard,
    BCardTitle,
    BBadge,
    BAvatar,
    BSpinner,
    VuePerfectScrollbar,
  },
  data() {
    return {
      agendamentos: [],
      loading: false,
      scrollSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
    }
  },
  computed: {
    agendamentosPorProximidade() {
      const agora = new Date().getTime()
      return this.agendamentos
        .sort((a, b) => {
          const dataA = new Date(a.data_agendamento).getTime()
          const dataB = new Date(b.data_agendamento).getTime()

          // Separar vencidos dos futuros
          const aVencido = dataA < agora
          const bVencido = dataB < agora

          // Se um é vencido e outro não, o não vencido vem primeiro
          if (aVencido && !bVencido) return 1
          if (!aVencido && bVencido) return -1

          // Se ambos são vencidos ou ambos são futuros, ordenar por proximidade
          return dataA - dataB
        })
    },

    agendamentosTotal() {
      return this.agendamentosPorProximidade.length
    },

    contadorProximidade() {
      return {
        vencido: this.agendamentosPorProximidade.filter(a => {
          const minutos = differenceInMinutes(new Date(a.data_agendamento), new Date())
          return minutos < 0
        }).length,
        critico: this.agendamentosPorProximidade.filter(a => {
          const minutos = differenceInMinutes(new Date(a.data_agendamento), new Date())
          return minutos >= 0 && minutos <= 5
        }).length,
        urgente: this.agendamentosPorProximidade.filter(a => {
          const minutos = differenceInMinutes(new Date(a.data_agendamento), new Date())
          return minutos > 5 && minutos <= 15
        }).length,
        aviso: this.agendamentosPorProximidade.filter(a => {
          const minutos = differenceInMinutes(new Date(a.data_agendamento), new Date())
          return minutos > 15 && minutos <= 30
        }).length,
        normal: this.agendamentosPorProximidade.filter(a => {
          const minutos = differenceInMinutes(new Date(a.data_agendamento), new Date())
          return minutos > 30
        }).length,
      }
    },
  },
  async mounted() {
    await this.carregarAgendamentos()
    // Recarregar a cada 30 segundos
    this.intervaloRefresh = setInterval(() => {
      this.carregarAgendamentos()
    }, 30000)
  },
  beforeDestroy() {
    if (this.intervaloRefresh) {
      clearInterval(this.intervaloRefresh)
    }
  },
  methods: {
    async carregarAgendamentos() {
      this.loading = true
      try {
        // Obter data de hoje em formato ISO (YYYY-MM-DD)
        const hoje = new Date()
        const dataHoje = hoje.toISOString().split('T')[0]

        // Obter ID do usuário atual do localStorage (userId = atendente_id para a API)
        const userId = localStorage.getItem('executivo_id')

        // Buscar agendamentos do dia atual do atendente
        const response = await agendamentosService.buscarAgendamentosCalendario({
          inicio: dataHoje,
          fim: dataHoje,
          atendente_id: userId,
        })

        // Extrair agendamentos da resposta (suportar múltiplas estruturas)
        let agendamentos = []
        if (response.data?.agendamentos) {
          agendamentos = response.data.agendamentos
        } else if (Array.isArray(response.data)) {
          agendamentos = response.data
        } else if (response.agendamentos) {
          agendamentos = response.agendamentos
        }

        this.agendamentos = agendamentos
      } catch (error) {
        this.agendamentos = []
      } finally {
        this.loading = false
      }
    },

    formatCliente,
    getStatusLabel,
    getMotivoLabel,

    getStatusBadgeVariant(status) {
      const badge = getStatusBadgeClass(status)
      return badgeVariantMap[badge] || 'secondary'
    },

    getMotivoBadgeVariant(motivo) {
      const badge = getMotivoBadgeClass(motivo)
      return badgeVariantMap[badge] || 'secondary'
    },

    getMinutosRestantes(dataAgendamento) {
      try {
        return differenceInMinutes(new Date(dataAgendamento), new Date())
      } catch (error) {
        return -1
      }
    },

    getClasseProximidade(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'agendamento-expired'
      }
      if (minutos <= 5) {
        return 'agendamento-critical'
      }
      if (minutos <= 15) {
        return 'agendamento-urgent'
      }
      if (minutos <= 30) {
        return 'agendamento-warning'
      }
      return 'agendamento-normal'
    },

    getVarianteAvatar(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'light-dark'
      }
      if (minutos <= 5) {
        return 'light-danger'
      }
      if (minutos <= 15) {
        return 'light-warning'
      }
      if (minutos <= 30) {
        return 'light-info'
      }
      return 'light-primary'
    },

    getBadgeProximidade(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'secondary'
      }
      if (minutos <= 5) {
        return 'danger'
      }
      if (minutos <= 15) {
        return 'warning'
      }
      if (minutos <= 30) {
        return 'info'
      }
      return 'primary'
    },

    getProximidadeLabel(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'Pendente'
      }
      if (minutos <= 5) {
        return '🚨 Agora!'
      }
      if (minutos <= 15) {
        return '⚠️ Em breve'
      }
      if (minutos <= 30) {
        return '⏰ Próximo'
      }
      return '📅 Agendado'
    },

    formatarHora(data) {
      try {
        return format(new Date(data), 'HH:mm', { locale: ptBR })
      } catch (error) {
        return 'Hora inválida'
      }
    },

    irParaNegociacao(clienteId) {
      this.$router.push({
        name: 'negociacao-cliente',
        params: { id: clienteId },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.agendamentos-card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07), 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e0e0e0;

  .agendamentos-list {
    .agendamento-item {
      padding: 1rem;
      border-bottom: 1px solid #f0f0f0;
      transition: background-color 0.2s, border-left 0.2s;
      border-left: 3px solid transparent;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.02);
      }

      &.agendamento-critical {
        border-left-color: #da534f;
        background-color: rgba(218, 83, 79, 0.03);

        &:hover {
          background-color: rgba(218, 83, 79, 0.08);
        }
      }

      &.agendamento-urgent {
        border-left-color: #f4a460;
        background-color: rgba(244, 164, 96, 0.03);

        &:hover {
          background-color: rgba(244, 164, 96, 0.08);
        }
      }

      &.agendamento-warning {
        border-left-color: #3498db;
        background-color: rgba(52, 152, 219, 0.03);

        &:hover {
          background-color: rgba(52, 152, 219, 0.08);
        }
      }

      &.agendamento-normal {
        border-left-color: #7367f0;

        &:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }
      }

      &.agendamento-expired {
        border-left-color: #95989a;
        opacity: 0.6;
        background-color: rgba(149, 152, 154, 0.05);

        &:hover {
          background-color: rgba(149, 152, 154, 0.08);
        }
      }
    }
  }

  .scrollable-container {
    overflow-y: auto;
  }

  .min-width-0 {
    min-width: 0;
  }

  .gap-2 {
    gap: 0.5rem;
  }

  .cursor-pointer {
    cursor: pointer;
  }
}
</style>
